/**
 * CODE-SIDE W2 screen copy override (client spec v3, 2026-07-15).
 *
 * The W2 employee PHQ flow REUSES the branded B2C screens; the spec copy differs
 * from the employer-config-driven B2C strings. Rather than mutate any employer
 * `siteConfig` data, these overrides are applied ONLY when W2 mode is active
 * (`useIsW2Mode()` / `useW2Mode.getState().active`) at the render seams that show
 * each screen's headline/body. Off W2, the paying B2C flow is byte-identical.
 *
 * Strings are applied VERBATIM from `spec-phq-flow.md` — do not paraphrase.
 * Straight apostrophes are intentional (they match the client spec).
 */

/**
 * The medical waive certification legalese. Re-exported as the canonical W2
 * constant from the already-shipped self-selection build so the PHQ waiver modal
 * (spec screen 2) and the self-selection flow certify against the SAME verbatim
 * text — and the same text is what `waivePhq` sends as `certificationText` (Q5).
 */
import { FormStepSections } from '@features/onboarding/enums/formSections.enum';

export { MEDICAL_WAIVE_CERTIFICATION } from './selection/constants/copy';

/**
 * Spec screen 2 — the initial waiver modal. An answer is REQUIRED; NEXT is hidden;
 * Submit is inactive (light gray `#ececec`) until a valid selection; choosing
 * "I waive coverage" reveals the certification checkbox and Submit activates only
 * once BOTH the waive option AND the certify checkbox are selected.
 */
export const W2_WAIVER_COPY = {
  headline: `Waiver of health coverage for employee and/or any eligible dependent(s) not enrolling.`,
  body: `To be considered for medical coverage, you'll need to complete a brief Personal Health Questionnaire (PHQ). If you choose to waive medical coverage, you won't need to complete a PHQ.`,
  coverageLabel: 'I want coverage and will complete the PHQ',
  waiveLabel: 'I waive coverage',
  submitLabel: 'Submit',
} as const;

/** Spec screen 1 — Sign In (reused B2C register screen, W2-mode entry). */
export const W2_SIGNIN_COPY = {
  headline: 'Welcome!',
  body: `Your employer has partnered with OneHealth Plus to offer you access to private, PPO medical coverage. Create an account to get started!`,
} as const;

/** Spec screen 4 — Welcome (for all employees who did not waive). The `\n`
 * renders as a paragraph break via the step layout's `formatText`. */
export const W2_WELCOME_COPY = {
  headline: 'Welcome to the OneHealth Plus Benefits Enrollment Platform!',
  body: `We've made enrolling in your benefits simple and stress-free. It usually takes just 5 minutes, and Alia, our friendly AI assistant, will guide you every step of the way.\nGrab a coffee, take a breath, and let's do this together!`,
} as const;

/**
 * W2 welcome-screen image — the SINGLE source for both welcome screens every
 * non-waived employee sees: the PHQ welcome (`WelcomeForm`, reused B2C step 1)
 * and the `/select-plans` welcome (`SelectionWelcome`).
 *
 * In W2 mode this ALWAYS wins, overriding any employer
 * `siteConfig.images.welcome` (captain, 2026-07-21) — a group-branded image
 * would apply to the PHQ welcome only (`SelectionWelcome` reads no siteConfig),
 * silently desyncing the two screens. B2C is untouched: `WelcomeForm` still
 * reads `siteConfig.images.welcome ?? ''` when the W2 flag is off.
 */
export const W2_WELCOME_IMAGE = '/images/welcome-new.png';

/** Spec screen 5 — About You. */
export const W2_ABOUT_YOU_COPY = {
  headline: 'A little information about you and your dependents',
  body: `On this page, you'll enter details about yourself and any dependents you would like to be covered under your benefits.`,
} as const;

/**
 * Spec screens 6–9 — the PHQ sub-steps, keyed by the same `questionId` the B2C
 * `getStepText` uses. `HealthQuestionForm` swaps to these when W2 mode is active;
 * any id not overridden falls back to the B2C `getStepText`.
 */
const W2_MEDICAL_CONDITIONS_BODY = `We get it — answering questions about medical conditions isn't exactly a party. But we need them to process your application. Think of it like an RSVP to great coverage. It's quick, secure, and we'll keep it smooth. Let's knock this out!`;

export const W2_PHQ_STEP_TEXT: Record<
  string,
  { title: string; description: string }
> = {
  // Screen 6 — PHQ 1.
  initialQuestions: {
    title: `Let's answer some quick health questions`,
    description: `This is what we call the Personal Health Questionnaire (PHQ). Give your best, honest answers. Don't worry, you'll be done in minutes.`,
  },
  pregnancy: {
    title: `Let's answer some quick health questions`,
    description: `This is what we call the Personal Health Questionnaire (PHQ). Give your best, honest answers. Don't worry, you'll be done in minutes.`,
  },
  // Screen 7 — PHQ 2 (no body copy in the spec).
  takingMedicationsNow: {
    title: 'Time to talk about medications',
    description: '',
  },
  // Screen 8 — PHQ 3 (Medical Conditions + RSVP body).
  surgeriesDiabetesHeartMental: {
    title: 'Medical Conditions',
    description: W2_MEDICAL_CONDITIONS_BODY,
  },
  medDetails: {
    title: 'Medical Conditions',
    description: W2_MEDICAL_CONDITIONS_BODY,
  },
  finalHealthConditions: {
    title: 'Medical Conditions',
    description: W2_MEDICAL_CONDITIONS_BODY,
  },
  otherHealthConditions: {
    title: 'Medical Conditions',
    description: '',
  },
  // Screen 9 — PHQ 4 (Disclaimer).
  privacyDisclosure: {
    title: 'And now, a word from our lawyers',
    description: `This section covers important legal information, disclosures, and how we protect your privacy. It's here to keep things transparent and help you understand your rights and how your information is used. Nothing sneaky — just the facts. Once you click Next, you're all done!`,
  },
};

/** W2 override for a PHQ sub-step; `undefined` → caller falls back to B2C copy. */
export function w2StepText(
  questionId: string | undefined
): { title: string; description: string } | undefined {
  if (!questionId) return undefined;
  return W2_PHQ_STEP_TEXT[questionId];
}

/**
 * Display steps for the employee PHQ progress bar: the 3 form steps + the
 * terminal. "Finish Up" is display-only — it is never a `useFormSteps` entry, so
 * it reads `future` throughout the flow (as B2C's own Finish Up cell does).
 * Single source for both the bar cells and the "STEP n / 4" counter total.
 */
export const W2_PHQ_STEP_LABELS = [
  FormStepSections.Welcome,
  FormStepSections.Dependents,
  FormStepSections.HealthQuestion,
  FormStepSections.Finish,
] as const;

/** Spec screen 10 — Final (non-waiver completion). */
export const W2_DONE_COMPLETE_COPY = {
  headline: 'Thank You!',
  body: `Your Personal Health Questionnaire has been submitted successfully.
Once your employer's application has been reviewed and a coverage decision has been made, we'll email you with the next steps. If coverage is approved, you'll receive a link to review the health plan options available to you and complete your enrollment.
Thank you for completing this important step, and we'll be in touch soon.`,
} as const;

/** Spec screen 3 — Final (employees who waive coverage). `{Firstname}` is
 * interpolated from the account; the `\n` is a paragraph break. */
export const W2_DONE_WAIVE_COPY = {
  headline: (firstName?: string) =>
    firstName ? `Thank You, ${firstName}!` : 'Thank You!',
  body: `You're All Set! Your waiver of health coverage has been received and recorded. No further action is required at this time.
Thank you for completing your enrollment.`,
} as const;

/**
 * Returning-employee lockout (spec "Hard rules"): a completed OR waived employee
 * who logs in again is gated here and goes no further. The body is applied
 * VERBATIM from the client doc.
 */
export const W2_DONE_RETURNING_COPY = {
  headline: 'Questionnaire Already Completed',
  body: `Your personal health questionnaire has already been completed. Please contact your employer if you wish to make any changes or updates.`,
} as const;

/**
 * Authenticated W2 stage-resolver terminals. These are deliberately distinct
 * from `W2_DONE_RETURNING_COPY`: an unavailable selection window, a missing
 * enrollment, or an unfinished PHQ does NOT mean the questionnaire was already
 * completed. Keep the reason-specific copy honest even when there is no action
 * the employee can take on this surface.
 */
export const W2_DONE_QUESTIONNAIRE_PENDING_COPY = {
  headline: 'Questionnaire Not Yet Completed',
  body: `Your personal health questionnaire still needs to be completed before you can choose plans. Please use the enrollment link from your employer to continue your questionnaire.`,
} as const;

export const W2_DONE_SELECTION_CLOSED_COPY = {
  headline: 'No Open Plan Selection',
  body: `Your employer doesn't have an open plan-selection window right now, so there's nothing you need to do here. If you expected to choose or change plans, please contact your benefits team.`,
} as const;

export const W2_DONE_SELECTION_UNAVAILABLE_COPY = {
  headline: `Plan Selection Isn't Available Here`,
  body: `Your employer isn't offering self-service plan selection for this enrollment, so there's nothing you need to do on this page. Please contact your benefits team if you have questions about your coverage.`,
} as const;

export const W2_DONE_NOT_ENROLLED_COPY = {
  headline: 'Enrollment Not Found',
  body: `We couldn't find a current employer enrollment connected to this account. Please contact your benefits team so they can confirm your enrollment details.`,
} as const;
